/**
 * AseoNotesSection — las 3 notas de un aseo (round 2026-07-05; documentos +
 * modo borrador en round 2026-07-06; experiencia móvil de subida en round
 * 2026-10-26).
 *
 * Componente ROLE-AGNÓSTICO con DOS modos:
 *
 *  1) MODO PERSISTIDO (`aseoId` es un id real): pide `GET /aseos/:id/notas` y
 *     renderiza SOLO las notas que el backend devuelve para el caller, usando
 *     `permisos` para decidir cuáles son editables. Cada texto/adjunto se guarda
 *     de inmediato por su propio endpoint. Sirve igual para:
 *       - el ASEADOR (ve "Nota para el aseador" solo lectura + "Observaciones
 *         del aseador" editable; NUNCA recibe la privada),
 *       - el ADMIN (ve y edita las 3).
 *
 *  2) MODO BORRADOR (`aseoId` == null, se pasa `draftRef`): el aseo AÚN NO
 *     existe (modal de creación). No hay id para subir archivos, así que las 3
 *     notas se editan en memoria (texto + archivos "staged") y el padre, tras
 *     crear el aseo, llama `draftRef.current.commit(nuevoId)` para persistirlas.
 *     Solo el admin crea aseos, así que en borrador se muestran las 3 editables.
 *
 * Cada nota = texto + adjuntos (fotos / videos / DOCUMENTOS: PDF, Office, texto).
 *
 * SUBIDA DE ARCHIVOS (modo persistido) — pensada para el celular del aseador:
 *  - Los archivos entran a una COLA GLOBAL (`colaSubidaStore.ts`): uno a la vez,
 *    con barra de progreso real, tiempo restante, Cancelar y Reintentar. Cerrar
 *    el panel no corta la subida.
 *  - Las fotos se optimizan antes (config admin, `compressImageFile`); los
 *    videos grandes suben por trozos reanudables (`subidaReanudable.ts`).
 *  - Aviso de datos móviles (round 2026-10-27): si los archivos que se agregan
 *    suman más de 50 MB y el navegador dice que la conexión es móvil, se pide
 *    confirmación antes de encolar (`AvisoDatosMoviles.tsx`, `utils/conexion.ts`).
 *  - Icono (i) «Cómo subir archivos» (NotasAyuda.tsx) en el encabezado de la primera nota
 *    editable del modo persistido: explica la subida en lenguaje simple.
 *  - Videos: cada tile recibe `videoLiviano` (previewUrl firmado); abrir las notas no pide
 *    ningún dato de video, el visor arranca con la versión liviana (ver NotaArchivoTile).
 *  - Entrada: en pantallas táctiles, botones grandes «Tomar foto» / «Grabar
 *    video» / «Elegir de la galería»; en escritorio, arrastrar + click + Ctrl+V
 *    (regla del proyecto, storage-y-visor.md §7.1).
 *
 * AUTOGUARDADO DEL TEXTO: debounce 900 ms + blur + al ocultar la página. Si el
 * guardado falla muestra «No se pudo guardar · Reintentar», reintenta solo con
 * backoff y al volver la conexión, y respalda cada cambio en localStorage
 * (`utils/borradorNota.ts`) hasta que el servidor confirme.
 */

import {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Typography, Tooltip, Input, Button, Spin, Image, Tag, Empty } from "antd";
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  LockOutlined,
  EyeOutlined,
  EditOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useClipboardPaste } from "../../../Components/DragDropUpload";
import { AuthContext } from "../../../context/AuthContext";
import {
  aseoNotesApi,
  type AseoNotesData,
  type AseoNote,
  type AseoNoteTipo,
  type AseoFileEntry,
} from "../../../services/aseos.api";
import { getMsg } from "../../../utils/antdStatic";
import {
  claveBorrador,
  esperaReintentoTexto,
  guardarBorrador,
  leerBorrador,
  limpiarBorrador,
} from "../../../utils/borradorNota";
import { NotaArchivoTile, kindOf, type Tile } from "./NotaArchivoTile";
import { NotaColaSubida } from "./NotaColaSubida";
import { NotaEntradaArchivos, validarPesoArchivo } from "./NotaEntradaArchivos";
import { useColaNotas } from "./colaSubidaStore";
import { useAvisoDatosMoviles } from "./AvisoDatosMoviles";
import NotasAyuda from "./NotasAyuda";
import {
  decidirRecuperacion,
  guardarBase,
  guardarBaseSiFalta,
  leerBase,
  limpiarBase,
} from "./respaldoBase";
import { useEsTactil } from "./useMediaQuery";
import "../../../Css/notas-movil.css";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Tipos aceptados por las notas del aseo: imágenes, videos y documentos
 * (PDF / Word / Excel / PowerPoint / texto / CSV). Debe mantenerse alineado
 * con la whitelist de multer en backend/src/routes/aseos.routes.js.
 */
export const ACCEPT_NOTE_FILES = [
  "image/*",
  "video/*",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
].join(",");

/** Handle imperativo del modo borrador (lo consume el modal de creación). */
export interface AseoNotesDraftHandle {
  /** Persiste texto + archivos staged en el aseo recién creado. */
  commit: (nuevoAseoId: number) => Promise<void>;
  /** ¿Hay algún texto o archivo cargado en el borrador? */
  hasContent: () => boolean;
}

/** Metadatos de presentación por tipo de nota. */
const NOTE_META: Record<
  AseoNoteTipo,
  { label: string; color: string; icon: React.ReactNode; tooltip: string }
> = {
  para_aseador: {
    label: "Nota para el aseador",
    color: "blue",
    icon: <EyeOutlined />,
    tooltip:
      "Instrucciones que el administrador dejó para este aseo. El aseador solo puede leerlas (no editarlas). Puede incluir fotos, videos y documentos de referencia.",
  },
  aseador: {
    label: "Observaciones del aseador",
    color: "green",
    icon: <EditOutlined />,
    tooltip:
      "Espacio del aseador: aquí el aseador escribe su información del aseo y sube sus fotos/videos/documentos de terreno. El administrador también puede verla y editarla.",
  },
  privada: {
    label: "Nota privada (solo admin)",
    color: "red",
    icon: <LockOutlined />,
    tooltip:
      "Nota interna del administrador. El aseador y el cliente NUNCA la ven. Úsala para apuntes que no deben salir de la administración.",
  },
};

/** Orden fijo de renderizado (las que no vengan simplemente no se muestran). */
const ORDER: AseoNoteTipo[] = ["para_aseador", "aseador", "privada"];

/**
 * Borra el respaldo local y, si de verdad se borró, también su texto base.
 * (Con `textoConfirmado` solo se borra si coincide: ver `limpiarBorrador`.)
 */
function limpiarRespaldo(clave: string, textoConfirmado?: string): void {
  limpiarBorrador(clave, textoConfirmado);
  if (!leerBorrador(clave)) limpiarBase(clave);
}

/** Estado visible del autoguardado de una nota. */
type EstadoGuardado = "saving" | "saved" | "error";

/** Archivo "staged" en modo borrador (aún no subido al backend). */
interface StagedFile {
  key: string;
  file: File;
  previewUrl: string; // object URL para preview/abrir
  kind: "image" | "video" | "doc";
  mimeType: string;
  originalName: string;
}

export default function AseoNotesSection({
  aseoId,
  draftRef,
  enablePaste = true,
  onChanged,
}: {
  /** Id real del aseo → modo persistido. `null`/`undefined` → modo borrador. */
  aseoId?: number | null;
  /** En modo borrador, ref que expone `commit(nuevoId)` al padre. */
  draftRef?: React.MutableRefObject<AseoNotesDraftHandle | null>;
  /** Si false, no monta el listener de Ctrl+V (útil si hay varias secciones). */
  enablePaste?: boolean;
  /** Se llama tras guardar/subir/borrar (para que el padre refresque si quiere). */
  onChanged?: () => void;
}) {
  const isDraft = aseoId == null;
  const tactil = useEsTactil();
  // Sin lanzar si no hay proveedor (tests): el id solo separa borradores locales por usuario.
  const userId = useContext(AuthContext)?.user?.id ?? null;

  // ── Estado modo PERSISTIDO ──
  const [data, setData] = useState<AseoNotesData | null>(null);
  const [loading, setLoading] = useState(!isDraft);
  const [drafts, setDrafts] = useState<Partial<Record<AseoNoteTipo, string>>>({});
  // Estado de autosave por nota: "saving" mientras persiste, "saved" un instante
  // después, "error" si falló (se reintenta solo y hay botón). Sin key = sin
  // actividad reciente.
  const [saveStatus, setSaveStatus] = useState<
    Partial<Record<AseoNoteTipo, EstadoGuardado>>
  >({});
  // Notas cuyo texto se recuperó del respaldo local (nunca llegó al servidor).
  const [recuperadas, setRecuperadas] = useState<AseoNoteTipo[]>([]);
  // Disparador para guardar de inmediato lo recuperado (una vez por carga).
  const porRecuperarRef = useRef<AseoNoteTipo[]>([]);
  const [tickRecuperar, setTickRecuperar] = useState(0);

  // Cola de subida (global; sobrevive al cierre del panel).
  const { items: colaItems, avisoPantalla, encolar, cola } = useColaNotas(
    isDraft ? null : aseoId,
  );

  // Aviso de datos móviles antes de encolar archivos pesados (solo modo persistido).
  const { confirmar: confirmarDatosMoviles, modal: modalDatosMoviles } = useAvisoDatosMoviles();

  // ── Estado modo BORRADOR ──
  const [draftNotes, setDraftNotes] = useState<
    Record<AseoNoteTipo, { texto: string; files: StagedFile[] }>
  >({
    para_aseador: { texto: "", files: [] },
    aseador: { texto: "", files: [] },
    privada: { texto: "", files: [] },
  });
  const stagedKeyRef = useRef(0);
  // Ref siempre-actual a draftNotes para revocar los object URLs al desmontar
  // (un useEffect con deps vacías capturaría el valor inicial vacío).
  const draftNotesRef = useRef(draftNotes);
  draftNotesRef.current = draftNotes;
  // Último texto conocido del server por nota. Sirve para, al REFRESCAR tras una
  // mutación (subir/borrar archivo, guardar otra nota), preservar el texto que
  // el usuario tenga sin guardar en OTRA nota en vez de pisarlo con el del
  // server (bug de pérdida de datos, round 2026-07-06).
  const serverTextsRef = useRef<Partial<Record<AseoNoteTipo, string>>>({});
  // Ref siempre-actual a `drafts` para poder hacer flush de textos sin guardar
  // al desmontar (cierre del modal) sin depender del closure del effect.
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  // Timers del autosave: debounce al escribir y reintento tras un fallo, por tipo.
  const saveTimersRef = useRef<
    Partial<Record<AseoNoteTipo, ReturnType<typeof setTimeout>>>
  >({});
  const retryTimersRef = useRef<
    Partial<Record<AseoNoteTipo, ReturnType<typeof setTimeout>>>
  >({});
  const fallosTextoRef = useRef<Partial<Record<AseoNoteTipo, number>>>({});
  // Serialización: un solo PUT de texto en vuelo por nota (si no, las respuestas
  // pueden llegar desordenadas y el servidor quedar con el texto más viejo).
  const enVueloRef = useRef<Partial<Record<AseoNoteTipo, boolean>>>({});
  const reflushRef = useRef<Partial<Record<AseoNoteTipo, boolean>>>({});
  // Cuántos guardados exitosos lleva cada nota: un refresco (GET) lanzado antes de
  // un guardado trae el texto de ANTES y no debe pisar el texto recién guardado.
  const guardadosRef = useRef<Partial<Record<AseoNoteTipo, number>>>({});
  // ¿El componente sigue montado? (el flush final y los reintentos lo consultan)
  const montadoRef = useRef(true);
  // Respaldos locales que chocan con un texto del servidor que cambió: se conserva
  // el del servidor y la aseadora elige (tipo → texto del respaldo).
  const [conflictos, setConflictos] = useState<Partial<Record<AseoNoteTipo, string>>>({});

  const claveDe = useCallback(
    (tipo: AseoNoteTipo) => claveBorrador(userId, aseoId ?? 0, tipo),
    [userId, aseoId],
  );

  const load = useCallback(
    async (fresh = true) => {
      if (isDraft || aseoId == null) return;
      if (fresh) setLoading(true);
      const versionesAntes = { ...guardadosRef.current };
      const res = await aseoNotesApi.get(aseoId);
      if (res?.data) {
        const newServer: Partial<Record<AseoNoteTipo, string>> = {};
        (Object.keys(res.data.notas) as AseoNoteTipo[]).forEach((t) => {
          newServer[t] = res.data!.notas[t]?.texto ?? "";
        });
        // Refresco atrasado: si una nota se guardó (o se está guardando) mientras
        // esta petición viajaba, su texto del servidor es VIEJO. Se conserva el
        // último texto confirmado y se adopta solo el resto de la respuesta.
        const obsoletas = new Set<AseoNoteTipo>();
        if (!fresh) {
          (Object.keys(newServer) as AseoNoteTipo[]).forEach((t) => {
            if (
              enVueloRef.current[t] ||
              (guardadosRef.current[t] ?? 0) !== (versionesAntes[t] ?? 0)
            ) {
              obsoletas.add(t);
            }
          });
        }
        const notasVigentes = { ...res.data.notas };
        obsoletas.forEach((t) => {
          const previa = notasVigentes[t];
          if (previa) {
            notasVigentes[t] = { ...previa, texto: serverTextsRef.current[t] ?? "" };
          }
          newServer[t] = serverTextsRef.current[t] ?? "";
        });
        setData({ ...res.data, notas: notasVigentes });
        if (fresh) {
          // Carga inicial (o cambio de aseo): adoptar el texto del server tal
          // cual... salvo que haya un respaldo local que nunca llegó al
          // servidor: ese texto es de la aseadora y se recupera.
          const inicial: Partial<Record<AseoNoteTipo, string>> = { ...newServer };
          const rec: AseoNoteTipo[] = [];
          const choques: Partial<Record<AseoNoteTipo, string>> = {};
          (Object.keys(res.data.notas) as AseoNoteTipo[]).forEach((t) => {
            if (!res.data!.permisos[t]) return;
            const b = leerBorrador(claveDe(t));
            if (!b) return;
            const delServidor = newServer[t] ?? "";
            if (b.texto === delServidor) {
              limpiarRespaldo(claveDe(t));
            } else if (decidirRecuperacion(delServidor, leerBase(claveDe(t))) === "recuperar") {
              inicial[t] = b.texto;
              rec.push(t);
              guardarBase(claveDe(t), delServidor);
            } else {
              // El servidor cambió mientras el texto de la aseadora estaba sin
              // guardar: NO se pisa. Se muestra el del servidor y ella elige.
              choques[t] = b.texto;
            }
          });
          setDrafts(inicial);
          setRecuperadas(rec);
          setConflictos(choques);
          if (rec.length) {
            porRecuperarRef.current = rec;
            setTickRecuperar((n) => n + 1);
          }
        } else {
          // Refresco tras mutación: solo adoptamos el texto del server en las
          // notas que el usuario NO estaba editando (draft == último server).
          const oldServer = serverTextsRef.current;
          setDrafts((prev) => {
            const next = { ...prev };
            (Object.keys(res.data!.notas) as AseoNoteTipo[]).forEach((t) => {
              if (obsoletas.has(t)) return;
              const dirty = (prev[t] ?? "") !== (oldServer[t] ?? "");
              if (!dirty) next[t] = newServer[t] ?? "";
            });
            return next;
          });
        }
        serverTextsRef.current = newServer;
      }
      if (fresh) setLoading(false);
    },
    [aseoId, isDraft, claveDe],
  );

  useEffect(() => {
    if (!isDraft) void load();
  }, [load, isDraft]);

  // Revoca todos los object URLs al desmontar (evita fugas de memoria).
  useEffect(() => {
    return () => {
      Object.values(draftNotesRef.current).forEach((n) =>
        n.files.forEach((f) => URL.revokeObjectURL(f.previewUrl)),
      );
    };
  }, []);

  // ── Handle imperativo del borrador ──
  useImperativeHandle(
    draftRef,
    () => ({
      hasContent: () =>
        ORDER.some(
          (t) => (draftNotes[t].texto ?? "").trim() || draftNotes[t].files.length > 0,
        ),
      commit: async (nuevoAseoId: number) => {
        // Persistimos secuencialmente: primero el texto (silent, sin toast),
        // luego cada archivo. Un error individual no aborta el resto — el aseo
        // ya está creado y el admin puede reintentar al editarlo.
        for (const tipo of ORDER) {
          const n = draftNotes[tipo];
          const texto = (n.texto ?? "").trim();
          if (texto) {
            await aseoNotesApi.updateTexto(nuevoAseoId, tipo, texto, {
              silent: true,
            });
          }
          for (const sf of n.files) {
            await aseoNotesApi.uploadArchivo(nuevoAseoId, tipo, sf.file);
          }
        }
      },
    }),
    [draftNotes],
  );

  // ── Cola de subida: refrescar la lista cuando un archivo termina o falla ──
  useEffect(() => {
    if (isDraft || aseoId == null) return undefined;
    return cola.alTerminar((item) => {
      if (item.aseoId !== aseoId) return;
      void load(false);
      onChanged?.();
    });
  }, [cola, isDraft, aseoId, load, onChanged]);

  // Si un archivo falla "al final" (corte justo al procesar) puede haberse
  // guardado igual: refrescar la lista para que no haya que adivinar.
  const erroresVistosRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    let hayNuevo = false;
    for (const i of colaItems) {
      if (i.estado === "error" && !erroresVistosRef.current.has(i.id)) {
        erroresVistosRef.current.add(i.id);
        hayNuevo = true;
      }
    }
    if (hayNuevo) void load(false);
  }, [colaItems, load]);

  // ── Autoguardado del texto ──
  // Persiste con debounce al dejar de escribir y de inmediato en onBlur.
  // `flushSaveTexto` cancela los timers pendientes y guarda ya; usa la respuesta
  // del endpoint para actualizar el estado local sin refetch. Si falla: estado
  // «error», reintento con backoff y respaldo en localStorage intacto.
  const flushSaveTexto = useCallback(
    async (tipo: AseoNoteTipo, opts?: { keepalive?: boolean }) => {
      if (aseoId == null) return;
      const timer = saveTimersRef.current[tipo];
      if (timer) {
        clearTimeout(timer);
        delete saveTimersRef.current[tipo];
      }
      const retry = retryTimersRef.current[tipo];
      if (retry) {
        clearTimeout(retry);
        delete retryTimersRef.current[tipo];
      }
      if (enVueloRef.current[tipo]) {
        reflushRef.current[tipo] = true; // se vuelve a guardar al terminar el PUT en curso
        return;
      }
      const texto = draftsRef.current[tipo] ?? "";
      if (texto === (serverTextsRef.current[tipo] ?? "")) {
        limpiarRespaldo(claveDe(tipo));
        setSaveStatus((s) => {
          if (s[tipo] !== "error") return s;
          const next = { ...s };
          delete next[tipo];
          return next;
        });
        return; // nada nuevo
      }
      enVueloRef.current[tipo] = true;
      setSaveStatus((s) => ({ ...s, [tipo]: "saving" }));
      let res: Awaited<ReturnType<typeof aseoNotesApi.updateTexto>> = null;
      try {
        res = await aseoNotesApi.updateTexto(aseoId, tipo, texto, {
          silent: true,
          // El autoguardado avisa con su propio texto en español: sin el toast
          // crudo del navegador («Failed to fetch») en cada reintento.
          sinToastError: true,
          keepalive: opts?.keepalive,
        });
      } finally {
        enVueloRef.current[tipo] = false;
      }
      if (res?.success) {
        serverTextsRef.current[tipo] = texto;
        guardadosRef.current[tipo] = (guardadosRef.current[tipo] ?? 0) + 1;
        fallosTextoRef.current[tipo] = 0;
        limpiarRespaldo(claveDe(tipo), texto);
        // Si el respaldo sigue (se siguió escribiendo), ahora parte de este texto.
        if (leerBorrador(claveDe(tipo))) guardarBase(claveDe(tipo), texto);
        setRecuperadas((r) => r.filter((t) => t !== tipo));
        setData((prev) =>
          prev
            ? {
                ...prev,
                notas: {
                  ...prev.notas,
                  [tipo]: { ...prev.notas[tipo], texto },
                },
              }
            : prev,
        );
        setSaveStatus((s) => ({ ...s, [tipo]: "saved" }));
        onChanged?.();
      } else {
        // Falló (sin señal, servidor caído...): quedó sin guardar. El texto sigue
        // a salvo en el respaldo local; se reintenta solo con backoff.
        const fallos = (fallosTextoRef.current[tipo] ?? 0) + 1;
        fallosTextoRef.current[tipo] = fallos;
        setSaveStatus((s) => ({ ...s, [tipo]: "error" }));
        // Desmontado: no se programan reintentos infinitos; el texto sigue en el
        // respaldo local y se recupera al reabrir las notas.
        if (montadoRef.current) {
          retryTimersRef.current[tipo] = setTimeout(() => {
            void flushSaveTexto(tipo);
          }, esperaReintentoTexto(fallos));
        }
      }
      if (reflushRef.current[tipo]) {
        reflushRef.current[tipo] = false;
        // Tras el cierre del panel el último envío debe poder terminar solo.
        void flushSaveTexto(tipo, montadoRef.current ? opts : { ...opts, keepalive: true });
      }
    },
    [aseoId, onChanged, claveDe],
  );

  const scheduleSaveTexto = useCallback(
    (tipo: AseoNoteTipo) => {
      const timer = saveTimersRef.current[tipo];
      if (timer) clearTimeout(timer);
      saveTimersRef.current[tipo] = setTimeout(() => {
        void flushSaveTexto(tipo);
      }, 900);
    },
    [flushSaveTexto],
  );

  // El texto del servidor cambió mientras el de este celular estaba sin guardar:
  // se muestra el del servidor y la aseadora elige cuál se queda.
  const usarTextoDelCelular = useCallback(
    (tipo: AseoNoteTipo) => {
      const local = conflictos[tipo];
      if (local === undefined) return;
      setConflictos((c) => {
        const next = { ...c };
        delete next[tipo];
        return next;
      });
      setDrafts((prev) => ({ ...prev, [tipo]: local }));
      guardarBorrador(claveDe(tipo), local);
      guardarBase(claveDe(tipo), serverTextsRef.current[tipo] ?? "");
      // Se guarda cuando el render ya dejó el texto en `draftsRef`.
      porRecuperarRef.current = [...porRecuperarRef.current, tipo];
      setTickRecuperar((n) => n + 1);
    },
    [conflictos, claveDe],
  );

  const dejarTextoDelServidor = useCallback(
    (tipo: AseoNoteTipo) => {
      const local = conflictos[tipo];
      setConflictos((c) => {
        const next = { ...c };
        delete next[tipo];
        return next;
      });
      // Solo borra el respaldo si sigue siendo ese texto (no lo que se escribió después).
      limpiarRespaldo(claveDe(tipo), local);
    },
    [conflictos, claveDe],
  );

  // Texto recuperado del respaldo local: se intenta guardar de una vez.
  useEffect(() => {
    if (isDraft || porRecuperarRef.current.length === 0) return;
    const tipos = porRecuperarRef.current;
    porRecuperarRef.current = [];
    tipos.forEach((t) => void flushSaveTexto(t));
  }, [isDraft, tickRecuperar, flushSaveTexto]);

  // Al volver la conexión o al ocultar la página (bloquear el celular, cambiar de
  // app) se guarda lo pendiente; `keepalive` deja que el PUT termine aunque la
  // página se esté yendo.
  useEffect(() => {
    if (isDraft || aseoId == null) return undefined;
    const pendientes = () =>
      ORDER.filter(
        (t) => (draftsRef.current[t] ?? "") !== (serverTextsRef.current[t] ?? ""),
      );
    const alVolverConexion = () => pendientes().forEach((t) => void flushSaveTexto(t));
    const alOcultar = () => {
      if (document.visibilityState === "hidden") {
        pendientes().forEach((t) => void flushSaveTexto(t, { keepalive: true }));
      }
    };
    window.addEventListener("online", alVolverConexion);
    document.addEventListener("visibilitychange", alOcultar);
    window.addEventListener("pagehide", alOcultar);
    return () => {
      window.removeEventListener("online", alVolverConexion);
      document.removeEventListener("visibilitychange", alOcultar);
      window.removeEventListener("pagehide", alOcultar);
    };
  }, [isDraft, aseoId, flushSaveTexto]);

  // Flush de textos sin guardar al desmontar (cierre del modal). La request no
  // depende del ciclo de vida del componente, así que el guardado se completa
  // igual aunque el modal ya no esté montado — red de seguridad extra al blur.
  // Si falla, el respaldo local sigue ahí y se recupera al reabrir.
  useEffect(() => {
    // Los objetos de timers son estables (solo se mutan sus claves).
    const saveTimers = saveTimersRef.current;
    const retryTimers = retryTimersRef.current;
    const enVuelo = enVueloRef.current;
    const reflush = reflushRef.current;
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
      if (isDraft || aseoId == null) return;
      ORDER.forEach((tipo) => {
        const timer = saveTimers[tipo];
        if (timer) clearTimeout(timer);
        const retry = retryTimers[tipo];
        if (retry) clearTimeout(retry);
        const texto = draftsRef.current[tipo] ?? "";
        if (texto !== (serverTextsRef.current[tipo] ?? "")) {
          if (enVuelo[tipo]) {
            // Ya hay un PUT de esta nota en vuelo: uno paralelo podría llegar al
            // servidor ANTES y dejar el texto viejo. Se serializa: al terminar el
            // en curso, `flushSaveTexto` (que sigue vivo por su cierre) manda el final.
            reflush[tipo] = true;
            return;
          }
          void aseoNotesApi
            .updateTexto(aseoId, tipo, texto, {
              silent: true,
              sinToastError: true,
              keepalive: true,
            })
            .then((res) => {
              if (res?.success) {
                limpiarRespaldo(claveBorrador(userId, aseoId, tipo), texto);
              }
            });
        }
      });
    };
    // `userId` no cambia durante la vida del panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aseoId, isDraft]);

  const deleteArchivo = useCallback(
    async (tipo: AseoNoteTipo, key: string) => {
      if (aseoId == null) return;
      const res = await aseoNotesApi.deleteArchivo(aseoId, tipo, key);
      if (res?.success) {
        await load(false);
        onChanged?.();
      } else {
        getMsg().error(
          typeof navigator !== "undefined" && navigator.onLine === false
            ? "Sin conexión a internet. No se pudo eliminar el archivo."
            : "No se pudo eliminar el archivo. Revisa tu conexión e inténtalo de nuevo.",
        );
      }
    },
    [aseoId, load, onChanged],
  );

  // ── Acciones modo borrador ──
  const addStagedFile = useCallback((tipo: AseoNoteTipo, file: File) => {
    const key = `staged-${++stagedKeyRef.current}`;
    const staged: StagedFile = {
      key,
      file,
      previewUrl: URL.createObjectURL(file),
      kind: kindOf(file.type),
      mimeType: file.type,
      originalName: file.name,
    };
    setDraftNotes((prev) => ({
      ...prev,
      [tipo]: { ...prev[tipo], files: [...prev[tipo].files, staged] },
    }));
  }, []);

  const removeStagedFile = useCallback((tipo: AseoNoteTipo, key: string) => {
    setDraftNotes((prev) => {
      const target = prev[tipo].files.find((f) => f.key === key);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return {
        ...prev,
        [tipo]: {
          ...prev[tipo],
          files: prev[tipo].files.filter((f) => f.key !== key),
        },
      };
    });
  }, []);

  /**
   * Archivos ya validados → cola (persistido) o staged (borrador).
   * En modo persistido, si suman más de 50 MB y la conexión es de datos móviles,
   * antes se pide confirmación; si la persona cancela, no se encola nada.
   * El borrador no sube nada todavía (se sube al crear el aseo), así que no pregunta.
   */
  const recibirArchivos = useCallback(
    async (tipo: AseoNoteTipo, files: File[]) => {
      if (isDraft) {
        files.forEach((f) => addStagedFile(tipo, f));
        return;
      }
      if (!(await confirmarDatosMoviles(files))) return;
      files.forEach((f) => encolar(tipo, f));
    },
    [isDraft, addStagedFile, encolar, confirmarDatosMoviles],
  );

  // ── ¿Qué notas mostrar y cuáles editables? ──
  const notesToShow = useMemo<AseoNoteTipo[]>(() => {
    if (isDraft) return ORDER; // admin autor de las 3
    return data ? ORDER.filter((t) => data.notas[t]) : [];
  }, [isDraft, data]);

  const isEditable = useCallback(
    (tipo: AseoNoteTipo) => (isDraft ? true : !!data?.permisos[tipo]),
    [isDraft, data],
  );

  const editableTipos = useMemo(
    () => notesToShow.filter((t) => isEditable(t)),
    [notesToShow, isEditable],
  );
  // Ctrl+V va a la PRIMERA nota editable (para el aseador siempre es la suya).
  const pasteTarget = editableTipos[0] ?? null;

  useClipboardPaste({
    active: enablePaste && !!pasteTarget,
    accept: ACCEPT_NOTE_FILES,
    onFile: (f) => {
      if (!pasteTarget) return;
      const error = validarPesoArchivo(f);
      if (error) {
        getMsg().error(error);
        return;
      }
      void recibirArchivos(pasteTarget, [f]);
    },
  });

  // El aviso de datos móviles va en todas las ramas: si las notas se recargan con el
  // aviso abierto, no puede quedar una consulta sin respuesta bloqueando las siguientes.
  if (!isDraft && loading) {
    return (
      <>
        <div style={{ textAlign: "center", padding: 16 }}>
          <Spin />
        </div>
        {modalDatosMoviles}
      </>
    );
  }
  if (!isDraft && !data) {
    return (
      <>
        <Empty
          description="No se pudieron cargar las notas. Revisa tu conexión e inténtalo de nuevo."
          data-testid="nm-error-carga"
        >
          <Button type="primary" size="large" onClick={() => void load()} data-testid="nm-reintentar-carga">
            Reintentar
          </Button>
        </Empty>
        {modalDatosMoviles}
      </>
    );
  }

  // ── Estado del autoguardado (debajo del texto) ──
  const renderEstadoTexto = (tipo: AseoNoteTipo, dirty: boolean) => {
    const st = saveStatus[tipo];
    const sinRed = typeof navigator !== "undefined" && navigator.onLine === false;
    return (
      <div className="nm-estado-texto" data-testid="nm-estado-texto" aria-live="polite">
        {st === "saving" ? (
          <Text type="secondary" style={{ fontSize: 13 }}>
            <Spin size="small" style={{ marginRight: 6 }} /> Guardando…
          </Text>
        ) : st === "error" ? (
          <span data-testid="nm-error-texto">
            <Text type="danger" style={{ fontSize: 13 }}>
              <WarningOutlined style={{ marginRight: 4 }} />
              {sinRed ? "Sin conexión · se guarda al volver" : "No se pudo guardar"} ·
            </Text>
            <Button
              type="link"
              size="small"
              onClick={() => void flushSaveTexto(tipo)}
              data-testid="nm-reintentar-texto"
            >
              Reintentar
            </Button>
            <div style={{ fontSize: 13, color: "#8c8c8c" }}>
              Tu texto sigue en este celular y se reintenta solo.
            </div>
          </span>
        ) : st === "saved" ? (
          <Text style={{ fontSize: 13, color: "#52c41a" }}>
            <CheckCircleOutlined style={{ marginRight: 4 }} /> Guardado
          </Text>
        ) : dirty ? (
          <Text type="secondary" style={{ fontSize: 13, fontStyle: "italic" }}>
            {recuperadas.includes(tipo) ? "Recuperamos tu texto sin guardar · " : ""}
            Se guarda solo…
          </Text>
        ) : null}
      </div>
    );
  };

  const renderNote = (tipo: AseoNoteTipo) => {
    const meta = NOTE_META[tipo];
    const editable = isEditable(tipo);

    // Fuente de texto + archivos según el modo.
    const persistedNote: AseoNote | undefined = !isDraft
      ? data?.notas[tipo]
      : undefined;
    const textoActual = isDraft
      ? draftNotes[tipo].texto
      : (drafts[tipo] ?? "");
    const textoGuardado = isDraft ? "" : (persistedNote?.texto ?? "");
    const dirty = !isDraft && textoActual !== textoGuardado;

    // Tiles normalizados.
    const tiles: Tile[] = isDraft
      ? draftNotes[tipo].files.map((f) => ({
          key: f.key,
          kind: f.kind,
          mimeType: f.mimeType,
          name: f.originalName,
          previewSrc: f.previewUrl,
          fullSrc: f.previewUrl,
        }))
      : (persistedNote?.archivos ?? []).map((a: AseoFileEntry) => {
          const k = kindOf(a.mimeType, a.kind);
          return {
            key: a.key,
            kind: k,
            mimeType: a.mimeType ?? "",
            name: a.originalName || "archivo",
            previewSrc: a.thumbUrl || a.url || "",
            fullSrc: a.url || a.thumbUrl || "",
            // Versión liviana del video (480p): el visor la abre primero. Es solo una URL
            // firmada; el tile no pide ningún dato del video hasta que se toca (preload=none).
            videoLiviano: k === "video" ? (a.previewUrl ?? null) : null,
          };
        });

    const colaDeEstaNota = colaItems.filter((i) => i.tipo === tipo);

    const setTextoDraft = (val: string) =>
      setDraftNotes((prev) => ({
        ...prev,
        [tipo]: { ...prev[tipo], texto: val },
      }));

    return (
      <div
        key={tipo}
        data-testid={`nm-nota-${tipo}`}
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
          background: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: 8,
          }}
        >
          <Tag
            color={meta.color}
            icon={meta.icon}
            style={{ marginRight: 0, fontSize: 13 }}
          >
            {meta.label}
          </Tag>
          <Tooltip title={meta.tooltip}>
            <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
          </Tooltip>
          {!editable && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              (solo lectura)
            </Text>
          )}
          {/* Una sola ayuda por panel: en la primera nota editable (la de la aseadora es
              siempre la suya). En borrador no sube nada todavía, así que no aplica. */}
          {!isDraft && editable && tipo === editableTipos[0] && (
            <span style={{ marginLeft: "auto" }}>
              <NotasAyuda tactil={tactil} />
            </span>
          )}
        </div>

        {/* Texto */}
        {editable ? (
          <>
            <TextArea
              className="nm-textarea"
              data-testid={tipo === "aseador" ? "nm-texto" : `nm-texto-${tipo}`}
              value={textoActual}
              onChange={(e) => {
                const val = e.target.value;
                if (isDraft) {
                  setTextoDraft(val);
                  return;
                }
                setDrafts((prev) => ({ ...prev, [tipo]: val }));
                // Respaldo local ANTES de tocar la red: si el guardado falla o la
                // app se cierra, el texto se recupera al reabrir.
                guardarBorrador(claveDe(tipo), val);
                // ...junto con la versión del servidor sobre la que se escribe: así,
                // al recuperarlo, se sabe si el servidor cambió mientras tanto.
                const baseServidor = serverTextsRef.current[tipo] ?? "";
                if (conflictos[tipo] !== undefined) guardarBase(claveDe(tipo), baseServidor);
                else guardarBaseSiFalta(claveDe(tipo), baseServidor);
                // Al escribir de nuevo, limpiamos el "Guardado"/error previo y
                // reprogramamos el autosave con debounce.
                setSaveStatus((s) => {
                  if (!s[tipo]) return s;
                  const next = { ...s };
                  delete next[tipo];
                  return next;
                });
                scheduleSaveTexto(tipo);
              }}
              onBlur={() => {
                if (!isDraft) void flushSaveTexto(tipo);
              }}
              placeholder={
                tipo === "aseador"
                  ? "Escribe aquí la información del aseo…"
                  : "Escribe la nota…"
              }
              autoSize={{ minRows: 2, maxRows: 6 }}
            />
            {/* Autoguardado: sin botón. Se persiste al dejar de escribir
                (debounce ~1s), al salir del campo, al ocultar la página y al
                volver la conexión. En borrador el texto se persiste al crear el
                aseo (no aplica). */}
            {!isDraft && conflictos[tipo] !== undefined && (
              <div className="nm-conflicto-texto" data-testid="nm-conflicto-texto" style={{ marginTop: 6 }}>
                <Text type="warning" style={{ fontSize: 13 }}>
                  <WarningOutlined style={{ marginRight: 4 }} />
                  Este texto cambió en el servidor mientras tenías cambios sin guardar en este
                  celular. Mostramos la versión del servidor.
                </Text>
                <div style={{ fontSize: 13, color: "#8c8c8c", whiteSpace: "pre-wrap" }}>
                  Tu texto: «{(conflictos[tipo] ?? "").slice(0, 160)}
                  {(conflictos[tipo] ?? "").length > 160 ? "…" : ""}»
                </div>
                <Button
                  size="small"
                  onClick={() => usarTextoDelCelular(tipo)}
                  data-testid="nm-conflicto-usar-mio"
                >
                  Usar mi texto
                </Button>
                <Button
                  type="link"
                  size="small"
                  onClick={() => dejarTextoDelServidor(tipo)}
                  data-testid="nm-conflicto-dejar-servidor"
                >
                  Dejar la versión del servidor
                </Button>
              </div>
            )}
            {!isDraft && renderEstadoTexto(tipo, dirty)}
          </>
        ) : textoGuardado ? (
          <Text style={{ whiteSpace: "pre-wrap" }}>{textoGuardado}</Text>
        ) : (
          <Text type="secondary">Sin texto.</Text>
        )}

        {/* Adjuntos */}
        {(tiles.length > 0 || editable || colaDeEstaNota.length > 0) && (
          <div style={{ marginTop: 10 }}>
            {tiles.length > 0 && (
              <Image.PreviewGroup>
                <div
                  className="nm-tiles"
                  data-testid="nm-tiles"
                  style={{ marginBottom: editable ? 8 : 0 }}
                >
                  {tiles.map((tile) => (
                    <NotaArchivoTile
                      key={tile.key}
                      tile={tile}
                      editable={editable}
                      onDelete={() =>
                        isDraft
                          ? removeStagedFile(tipo, tile.key)
                          : void deleteArchivo(tipo, tile.key)
                      }
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            )}
            {!isDraft && (
              <NotaColaSubida
                items={colaDeEstaNota}
                avisoPantalla={avisoPantalla}
                onCancelar={cola.cancelar}
                onReintentar={cola.reintentar}
                onQuitar={cola.quitar}
                onDescartarAviso={cola.descartarAvisoPantalla}
              />
            )}
            {editable && (
              <NotaEntradaArchivos
                tactil={tactil}
                accept={ACCEPT_NOTE_FILES}
                onFiles={(files) => void recibirArchivos(tipo, files)}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  if (notesToShow.length === 0) {
    return (
      <>
        <Empty description="Sin notas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        {modalDatosMoviles}
      </>
    );
  }

  return (
    <div>
      {notesToShow.map((t) => renderNote(t))}
      {modalDatosMoviles}
    </div>
  );
}
